import { squareName } from './board';
import { faceCode } from './cards';
import type { Face, Inscription, PileEntry, RecordedLink, RecordedMove, Square } from './types';

/**
 * Notation (spec §3.6): squares "C4", faces "R2", Wilds "W" / "W=R2",
 * links "R2 A4-A6x+", Yellow "Y2 A2-C4x-E6x+".
 */

export const faceNotation = (face: Face, wasWild = false): string =>
  wasWild ? `W=${faceCode(face)}` : faceCode(face);

export function linkNotation(
  face: Face,
  wasWild: boolean,
  from: Square,
  landings: readonly Square[],
  captureAt: readonly boolean[],
  crowned: boolean,
): string {
  // A Trick's link lands nowhere: just the face and the chain pawn's square.
  if (landings.length === 0) return `${faceNotation(face, wasWild)} ${squareName(from)}`;
  const path = landings.map((l, i) => `${squareName(l)}${captureAt[i] ? 'x' : ''}`).join('-');
  return `${faceNotation(face, wasWild)} ${squareName(from)}-${path}${crowned ? '+' : ''}`;
}

export const pileEntryNotation = (entry: PileEntry): string =>
  entry.source === 'card'
    ? faceNotation(entry.face, entry.wasWild)
    : entry.source === 'chalk'
      ? `${faceCode(entry.face)} (Chalk)`
      : `${faceCode(entry.face)} (Epitaph)`;

const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

export function movementNotation(move: RecordedMove): string {
  if (move.type === 'hops')
    return move.dirs.map((d) => `(${signed(d.dx)}, ${signed(d.dy)})`).join(' ');
  if (move.type === 'knights')
    return move.jumps.map((j) => `(${signed(j.dx)}, ${signed(j.dy)})`).join(' ');
  if (move.type === 'still') return '(0, 0)';
  return `(${signed(move.dx)}, ${signed(move.dy)})`;
}

/** Each step of a recorded move as a displacement: hops and jumps one by one. */
export function moveSteps(move: RecordedMove): { dx: number; dy: number }[] {
  if (move.type === 'hops') return move.dirs.map((d) => ({ dx: 2 * d.dx, dy: 2 * d.dy }));
  if (move.type === 'knights') return move.jumps.map((j) => ({ dx: j.dx, dy: j.dy }));
  if (move.type === 'still') return [];
  return [{ dx: move.dx, dy: move.dy }];
}

/** e.g. "R3 (0, +3)" or "W=R2 (0, +2)". */
export const recordedLinkNotation = (link: RecordedLink): string =>
  `${faceNotation(link.face, link.wasWild)} ${movementNotation(link.move)}`;

/** e.g. "[R3 (0, +3), R2 (0, +2)]". */
export const inscriptionNotation = (inscription: Inscription): string =>
  `[${inscription.links.map(recordedLinkNotation).join(', ')}]`;

/** Net displacement of a recorded route, in board coordinates. */
export function routeDisplacement(links: readonly RecordedLink[]): { dx: number; dy: number } {
  let dx = 0;
  let dy = 0;
  for (const l of links)
    for (const step of moveSteps(l.move)) {
      dx += step.dx;
      dy += step.dy;
    }
  return { dx, dy };
}

/** Relative landing offsets of a recorded route (for drawing route glyphs). */
export function routeOffsets(links: readonly RecordedLink[]): { dx: number; dy: number }[] {
  const out: { dx: number; dy: number }[] = [];
  let dx = 0;
  let dy = 0;
  for (const l of links)
    for (const step of moveSteps(l.move)) {
      dx += step.dx;
      dy += step.dy;
      out.push({ dx, dy });
    }
  return out;
}
